import { Camera, Check, Repeat, X } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";

interface Props {
  value: Blob | null;
  onChange: (value: Blob | null) => void;
}

export function CameraCapture({ value, onChange }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [active, setActive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [ready, setReady] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const stop = () => {
    stopTracks();
    setActive(false);
    setStarting(false);
    setReady(false);
  };

  useEffect(() => () => stopTracks(), []);

  useEffect(() => {
    if (!value) {
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      return;
    }
    const next = URL.createObjectURL(value);
    setPreview(next);
    return () => URL.revokeObjectURL(next);
  }, [value]);

  useEffect(() => {
    if (!active) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;

    let mounted = true;
    const markReady = () => {
      if (mounted && video.videoWidth > 0 && video.videoHeight > 0) setReady(true);
    };
    const timeout = window.setTimeout(() => {
      if (!mounted || video.videoWidth > 0) return;
      setError("The camera is taking too long to start. Close it and try again, or continue without a selfie.");
      stop();
    }, 10_000);

    video.srcObject = stream;
    video.addEventListener("loadedmetadata", markReady);
    video.addEventListener("loadeddata", markReady);
    video.addEventListener("playing", markReady);
    void video.play().then(markReady).catch(() => {
      if (!mounted) return;
      setError("The camera could not start. Close it and try again, or continue without a selfie.");
      stop();
    });

    return () => {
      mounted = false;
      window.clearTimeout(timeout);
      video.removeEventListener("loadedmetadata", markReady);
      video.removeEventListener("loadeddata", markReady);
      video.removeEventListener("playing", markReady);
      video.srcObject = null;
    };
  }, [active]);

  const start = async () => {
    setError(null);
    setStarting(true);
    setReady(false);
    try {
      stopTracks();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: { ideal: 1440 },
          height: { ideal: 1800 },
        },
      });
      streamRef.current = stream;
      setActive(true);
    } catch {
      setError("Camera access is needed to take a portrait. You can continue without one.");
    } finally {
      setStarting(false);
    }
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!ready || !video || !video.videoWidth || !video.videoHeight) {
      setError("Wait for the camera preview to appear, then try again.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = 1500;
    const context = canvas.getContext("2d");
    if (!context) return;

    const sourceRatio = video.videoWidth / video.videoHeight;
    const targetRatio = canvas.width / canvas.height;
    let sx = 0;
    let sy = 0;
    let sw = video.videoWidth;
    let sh = video.videoHeight;
    if (sourceRatio > targetRatio) {
      sw = video.videoHeight * targetRatio;
      sx = (video.videoWidth - sw) / 2;
    } else {
      sh = video.videoWidth / targetRatio;
      sy = (video.videoHeight - sh) / 2;
    }
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
    if (blob) onChange(blob);
    stop();
  };

  return (
    <section className="camera" aria-labelledby="camera-title">
      <div className="field-heading">
        <div>
          <span className="eyebrow">Portrait</span>
          <h2 id="camera-title">Take a selfie</h2>
        </div>
        <span className="optional">Optional</span>
      </div>
      <p className="field-help camera__explanation">
        Completely optional. Your identity stays yours. If you take a selfie, it will be restyled with AI effects to match the visual language of the installation.
      </p>

      {!active && !preview && (
        <button className="camera__start" type="button" onClick={start} disabled={starting}>
          <Camera size={26} weight="bold" aria-hidden="true" />
          {starting ? "Starting camera…" : "Open camera"}
        </button>
      )}

      {active && (
        <div className="camera__stage">
          <video ref={videoRef} muted playsInline aria-label="Camera preview" />
          {!ready && <div className="camera__loading" role="status">Starting camera…</div>}
          <div className="camera__guide" aria-hidden="true" />
          <button className="camera__shutter" type="button" onClick={capture} disabled={!ready} aria-label="Take photo">
            <span />
          </button>
          <button className="icon-button camera__close" type="button" onClick={stop} aria-label="Close camera">
            <X size={22} weight="bold" />
          </button>
        </div>
      )}

      {preview && (
        <div className="camera__result">
          <img src={preview} alt="Your captured portrait" />
          <span className="camera__confirmed"><Check size={18} weight="bold" /> Captured</span>
          <div className="camera__result-actions">
            <button className="button button--light" type="button" onClick={start}>
              <Repeat size={20} weight="bold" /> Retake
            </button>
            <button className="button button--ghost-light" type="button" onClick={() => onChange(null)}>
              Remove
            </button>
          </div>
        </div>
      )}

      {error && <p className="field-error" role="alert">{error}</p>}
    </section>
  );
}
